import { InvoiceLine, ServiceTransaction } from '../types';
import {
    SchattenAmTorDeps,
    SchattenAmTorEingang,
    STANDARD_TOR_DEPS,
    TorDiff,
    TorJournalWeg,
    TorKopf,
    TorMeldung,
    TorSchnappschuss,
    TorVergleich,
    abbildbareLeistungen as abbildbareLeistungenIn,
    abweichungsMeldung,
    istPositiveLeistungszeile,
    journalwegAmTor,
    kappeStack,
    schnappschussAmTor,
    sicherMelden,
    torKopf,
    torMeldungsTitel,
} from './allocationGateShadow';
import { ServiceJournalRow, isJournalMappable } from './serviceJournal';
import { buchungstag, kennungFuerVergleich } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DAS TOR (S5a Stufe 3b) — die Zuordnung kommt aus dem Buchungsjournal
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis Stufe 3a fror `planProjectIssue` die Zuordnung aus dem Projekt-JSON ein
 * und rechnete sie nebenher, ohne Wirkung, aus dem Journal nach
 * (allocationGateShadow.ts). Gemessen in Stufe 2 (05.–16.10.2026, 62 Belege)
 * und 3a (18.–23.10.2026, 76 Rechnungen): keine einzige Abweichung, kein
 * Lesefehler, kein Ausfall. Betreiber-Go fuer Stufe 3 am 18.10.
 *
 * Jetzt entscheidet das Journal. Beim Ausstellen rechnet das Tor BEIDE Wege
 * mit denselben Eingaben (Zeilen, vergebene Mengen der Vorbelege, Zeitraum,
 * Zuwachsfunktion, LV, Wahl „ohne Datum mitnehmen") und vergleicht sie mit
 * derselben reinen Funktion wie der Schatten (`vergleicheTorZuordnung`) —
 * es gibt keine zweite Vergleichslogik. Dann:
 *
 *   - gleich           → die Zuordnung des JOURNAL-Wegs wird eingefroren.
 *                        Das ist das Umschalten. Gemeldet wird nichts.
 *   - Abweichung mit Geldwirkung → GESPERRT, vor jedem Schreiben
 *                        (`TorGesperrtError`, Code `TOR_GESPERRT`), dazu eine
 *                        Meldung nach `bug_reports` (Entscheid 2: hart, auch
 *                        am Versandtag, kein Umgehen je Rechnung).
 *   - Journal nicht lesbar → GESPERRT („Buchungsjournal nicht lesbar — bitte
 *                        erneut versuchen", Entscheid 3: kein stiller
 *                        Rueckfall auf das JSON).
 *   - der Vergleich selbst faellt aus → GESPERRT (fail closed).
 *
 * ─── Was sperrt (`torEntscheid`) ───────────────────────────────────────────
 * Jede Abweichung, die der Schatten heute meldete (`diffs`) — sie hat die
 * Geldprobe des Vergleichs schon bestanden. Gleichstaende (`getauscht`) und
 * Unterschiede ohne Geldwirkung (`ohneGeldwirkung`) stehen gar nicht in
 * `diffs` und sperren nie. Zwei Ausnahmen, beide ohne Geld:
 *   1. `undatiert_weicht_ab` sperrt nur bei `details.includeUndated === true`
 *      (Entscheid 18.10.). Fehlt die Wahl (Altentwurf), kann der Entwurf
 *      ebenso gut ohne die Option gebaut sein — gemeldet wird trotzdem.
 *   2. `zeitraum_menge_weicht_ab` einer Leistung, der DIESER Beleg nichts
 *      zuordnet: Storno, Gutschrift, reine Betragskorrektur und eine
 *      kumulative Zeile ohne Zuwachs reicht `planAllocations` auf beiden
 *      Wegen unveraendert durch. Ihr Betrag haengt an keinem Zeitraumdeckel —
 *      der Deckel ist eine Groesse des Entwurfsaufbaus, und der Entwurf dieser
 *      Belege kommt aus dem Ursprungsbeleg. Ohne die Ausnahme stuende ein
 *      Storno still, nur weil das Journal einer heute erfassten Leistung im
 *      Zeitraum des Originals nachlaeuft. Gemeldet wird auch das.
 * Eine Meldung ohne Sperre heisst „Tor ohne Sperre"; eingefroren wird dann
 * die Journal-Zuordnung — sie ist auf jeder Zeile, die etwas zuordnet,
 * dieselbe wie im JSON (sonst gaebe es dort einen Eintragsbefund, und der
 * sperrt).
 *
 * ─── Was sich am Eingefrorenen aendert, wenn es „gleich" ist ──────────────
 * Nichts an Menge und Tag: jeder Eintrag, der nur auf einer Seite steht oder
 * mit anderer Menge oder anderem Berliner Tag zugeordnet wird, ist ein
 * Befund. Auch die Schreibweise der ids nicht: der Vergleich schluesselt
 * roh — eine im JSON grossgeschriebene uuid, die aus dem Journal klein
 * zurueckkommt, ist dort `eintrag_fehlt_im_journal` + `eintrag_nur_im_journal`
 * und SPERRT (Hinweis „nur die Schreibweise der uuid weicht ab"). Was bei
 * „gleich" uebrig bleibt, ist der GLEICHSTAND: zwei Eintraege derselben
 * Leistung am selben Berliner Tag, auf beiden Seiten mit derselben roh
 * geschriebenen id, derselben erbrachten Menge, beide abrechenbar und mit
 * gleicher Tagessumme — dann friert der Journal-Weg vielleicht den anderen
 * der beiden ein. Gleiches Geld, gleicher Tag, gleicher Monat; alles
 * Nachgelagerte (allocatedByEntry, Storno per Spiegelung, Teilruecknahme,
 * Nachtschatten, Monatsuebersicht, DATEV-Monat, GoBD-Monatsaufteilung) liest
 * die Zuordnung je Eintrag ueber `kennungFuerVergleich` bzw. nur ihren Tag
 * und ihre Menge — der andere Zwilling gilt dort einfach als der abgerechnete.
 *
 * ─── Reihenfolge und Wiederholung ──────────────────────────────────────────
 * Das Tor steht in `planProjectIssue` direkt nach der JSON-Zuordnung und VOR
 * der Idempotenz-Schicht, den Riegeln, `planWriteBack`, der Sperrfrage und
 * dem Speichern. Vorher ist an diesem Beleg nur der Statusanspruch ISSUING
 * geschrieben — den setzt der catch von `issueInvoice` zurueck — und bei
 * einer Sammelrechnung die Rueckschreibung der Projekte davor, die dieselbe
 * Kompensation zuruecknimmt (genau wie bei den Pauschalriegeln). Nummer,
 * PDF, Snapshot und Journal-Zweitschrift kommen alle erst danach.
 *
 * Ein Wiederholungsversuch einer bereits gebuchten Ausstellung
 * (`isAlreadyBooked`) wird NICHT an einer Abweichung gesperrt: die eigene
 * erste Buchung steht schon im JSON, ihre Zweitschrift ins Journal aber
 * erst, wenn der Beleg steht — sie liegt also gerade nicht im Journal.
 * Gebucht wird dabei nichts mehr; eingefroren wird IMMER die JSON-Zuordnung
 * (wie bis Stufe 3a, auch bei „gleich" — Codex-Review 3b), eine Abweichung
 * gemeldet als „Tor ohne Sperre". Ein Lesefehler sperrt auch die
 * Wiederholung: er stammt nicht aus der eigenen Buchung, und erneut
 * versuchen ist genau der Rat.
 *
 * ─── Durchgereichte Belege: kein Tor (Controller-Entscheid 23.10.2026) ────
 * Traegt ein Beleg in einem Projekt KEINE Leistungszeile mit positiver Menge
 * (`nurDurchgereicht`: Storno, kaufmaennische Gutschrift, reine
 * Betragskorrektur), laeuft dieses Projekt wie im JSON-Modus: JSON-Zeilen und
 * der nicht blockierende Schatten, der nur meldet. Auch bei Lesefehler,
 * leerem Journal oder Ausfall wird dann nicht gesperrt. Begruendung: die
 * Zuordnung dieser Zeilen ist gespiegelt oder leer und auf beiden Wegen
 * dieselbe; das Tor schuetzt dort kein Geld, eine Sperre verhinderte nur,
 * dass eine falsche Rechnung korrigiert wird.
 * NICHT ausgenommen ist die kumulative Zeile ohne Zuwachs (Review 3b, P1):
 * `planAllocations` reicht sie zwar durch, aber ihre Zeilenmenge IST der
 * abgerechnete Stand, und genau den pruefen die Groessen aus Stufe 3a
 * (Stichtag, Menge ohne Datum, Buchungsart). Fehlt im JSON ein Eintrag, den
 * das Journal kennt, stuende der Beleg sonst mit zu kleinem Stand da.
 * Entschieden in planProjectIssue, BEVOR das Tor laeuft; `torAmTor` selbst
 * bleibt fuer gemischte Belege bei der Ausnahme fuer den Zeitraumdeckel
 * (oben).
 *
 * ─── Ein leeres Journal ist kein Befund, sondern nicht lesbar ─────────────
 * Lesen darf das Journal nur, wer `is_admin()` oder `can_access_project`
 * erfuellt (service_journal.sql) — das Projekt selbst liest dagegen jeder
 * Interne. Wer mit dem Recht „Rechnungen ausstellen", aber ohne
 * Projektzugriff ausstellt, bekommt von PostgREST KEINE Zeile und keinen
 * Fehler. Daraus entstuenden lauter „fehlt im Journal"-Befunde mit dem Rat,
 * es in Minuten erneut zu versuchen — der nie hilft. Deshalb: das Journal
 * ist leer, das Projektprotokoll fuehrt aber abbildbare LEISTUNGEN (keine
 * Buchungsvorgaenge — die eigenen BU-Zeilen einer Wiederholung stehen dort
 * zu Recht noch nicht) → gesperrt als „nicht lesbar" (Entscheid 3), mit dem
 * Rat, der wirklich hilft (Projekt oeffnen und den Zugriff freischalten —
 * oder einen Administrator ausstellen lassen). RLS wirkt je Projekt ganz oder
 * gar nicht; ein Teil-Journal kann daraus nicht entstehen.
 */

/** Wie in allocationLogic: darunter ist eine Menge Rundungsrauschen. */
const EPS = 0.0005;

/** Der Code der Sperre — die Oberflaeche erkennt sie daran, nicht am Text. */
export const TOR_GESPERRT = 'TOR_GESPERRT' as const;

export type TorSperrGrund = 'abweichung' | 'journal_nicht_lesbar' | 'ausfall';

/** Was die Oberflaeche zeigt: ein Satz, die Positionen, der Rat. */
export interface TorSperrAnzeige {
    titel: string;
    punkte: string[];
    hinweis: string;
}

const anzeigeAlsText = (a: TorSperrAnzeige): string =>
    [a.titel, ...a.punkte.map((p) => `• ${p}`), a.hinweis].filter((z) => z !== '').join('\n');

/** Die Sperre am Tor. Die Meldung ist fertiger Klartext fuer den Nutzer. */
export class TorGesperrtError extends Error {
    readonly code: typeof TOR_GESPERRT = TOR_GESPERRT;

    constructor(
        readonly grund: TorSperrGrund,
        readonly anzeige: TorSperrAnzeige,
        readonly projectId: string,
    ) {
        super(anzeigeAlsText(anzeige));
        this.name = 'TorGesperrtError';
    }
}

/** Ist das die Sperre am Tor? Auch ueber Modulgrenzen hinweg am Code erkennbar. */
export const istTorGesperrt = (e: unknown): e is TorGesperrtError =>
    e instanceof TorGesperrtError ||
    (typeof e === 'object' && e !== null && (e as { code?: unknown }).code === TOR_GESPERRT);

/**
 * Was die Oberflaeche zu einem Fehler zeigt — oder null, wenn er keine Sperre
 * am Tor ist. Eine Sperre ohne lesbare Anzeige zeigt ihre Meldung als Titel.
 */
export const torSperrAnzeige = (e: unknown): TorSperrAnzeige | null => {
    if (!istTorGesperrt(e)) return null;
    const a = (e as { anzeige?: Partial<TorSperrAnzeige> }).anzeige;
    if (a && typeof a.titel === 'string' && Array.isArray(a.punkte) && typeof a.hinweis === 'string') {
        return { titel: a.titel, punkte: a.punkte.filter((p): p is string => typeof p === 'string'), hinweis: a.hinweis };
    }
    const text = typeof (e as { message?: unknown }).message === 'string' ? (e as { message: string }).message : '';
    return { titel: text || 'Ausstellen am Rechnungstor gesperrt.', punkte: [], hinweis: '' };
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Entscheidung — rein
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Leistungen, denen dieser Beleg etwas ZUORDNET — dieselbe Schranke wie
 * in `planAllocations`: Leistungszeile, keine reine Betragskorrektur, und die
 * zuzuordnende Menge (Zuwachs bzw. Zeilenmenge) nicht <= 0. Storno- und
 * Gutschriftzeilen (negativ) und kumulative Zeilen ohne Zuwachs fallen heraus.
 * Eine Menge, die keine Zahl ist (NaN), zaehlt MIT — `planAllocations` fragt
 * `open <= EPS` und laesst sie ebenso nicht durch.
 */
export const zuzuordnendeLeistungen = (
    zeilen: InvoiceLine[],
    quantityOf?: (line: InvoiceLine) => number,
): Set<string> => {
    const out = new Set<string>();
    for (const l of zeilen ?? []) {
        if (l?.kind !== 'SERVICE' || !l.serviceId || l.quantityInformational) continue;
        const menge = quantityOf ? quantityOf(l) : (l.quantity ?? 0);
        if (!(Number(menge) <= EPS)) out.add(l.serviceId);
    }
    return out;
};

/**
 * Traegt der Beleg in diesem Projekt KEINE Leistungszeile mit positiver
 * Menge? Dann laeuft das Tor gar nicht erst — der Aufrufer nimmt die
 * JSON-Zeilen und laesst nur den Schatten melden (Controller-Entscheid
 * 23.10.2026, Kopf „Durchgereichte Belege").
 *
 * Abgeleitet aus der Zeilenlogik, NICHT aus der Belegart: ein Storno und eine
 * kaufmaennische Gutschrift tragen negative Zeilen, eine reine
 * Betragskorrektur `quantityInformational`. Solche Zeilen reicht
 * `planAllocations` auf beiden Wegen unveraendert durch (die Menge ist <= 0,
 * kumulativ auch der Zuwachs `max(0, …)`), und Stufe 3a prueft an ihnen
 * nichts. Durchs Tor gehen dagegen: jede Zeile mit positiver Menge — auch
 * eine Korrektur nach OBEN, auch die Gutschrift im umsatzsteuerlichen Sinn
 * (SELF_BILLING_CREDIT_NOTE, positiver Umsatz), und die kumulative Zeile ohne
 * Zuwachs: ihr Stand ist die Zeilenmenge, und den pruefen die Groessen aus
 * Stufe 3a (Kopf, Review 3b P1). Ein Projekt ohne jede Leistungszeile
 * erreicht das Tor nicht (groupLinesByProject).
 *
 * Zusaetzlich darf `planAllocations` mit derselben Zuwachsfunktion nichts
 * NEU zuordnen (`zuzuordnendeLeistungen`). Bei heilem Projekt folgt das aus
 * der Zeilenmenge; bei einem kaputten Abrechnungsstand (negativ, keine Zahl)
 * kann der Zuwachs aber ueber der Zeilenmenge liegen — dann prueft das Tor
 * (Review 3b, P3).
 */
export const nurDurchgereicht = (
    zeilen: InvoiceLine[],
    quantityOf?: (line: InvoiceLine) => number,
): boolean =>
    !(zeilen ?? []).some(istPositiveLeistungszeile) && zuzuordnendeLeistungen(zeilen, quantityOf).size === 0;

export interface TorEntscheid {
    /** Abweichungen, die die Ausstellung anhalten. */
    sperrend: TorDiff[];
    /** Abweichungen, die nur gemeldet werden (Begruendung im Kopf). */
    ohneSperre: TorDiff[];
}

export const torEntscheid = (
    ergebnis: Pick<TorVergleich, 'diffs'>,
    rahmen: { includeUndated?: boolean; zuzuordnen: ReadonlySet<string> },
): TorEntscheid => {
    const sperrend: TorDiff[] = [];
    const ohneSperre: TorDiff[] = [];
    for (const d of ergebnis.diffs ?? []) {
        if (d.art === 'undatiert_weicht_ab' && rahmen.includeUndated !== true) {
            ohneSperre.push(d);
        } else if (d.art === 'zeitraum_menge_weicht_ab' && !rahmen.zuzuordnen.has(d.serviceId ?? '')) {
            ohneSperre.push(d);
        } else {
            sperrend.push(d);
        }
    }
    return { sperrend, ohneSperre };
};

// ═══════════════════════════════════════════════════════════════════════════
// Was der Nutzer liest
// ═══════════════════════════════════════════════════════════════════════════

/** Hoechstens so viele Positionen im Dialog; der Rest wird gezaehlt. */
const MAX_PUNKTE = 8;

const projektName = (kopf: TorKopf): string => kopf.projectNumber ?? kopf.projectId;

/** Eine Menge deutsch geschrieben — „1,5", nie „NaN" als Wurf. */
const menge = (n: number | string | null): string => {
    const x = Number(n);
    return Number.isFinite(x) ? x.toLocaleString('de-DE', { maximumFractionDigits: 3 }) : String(n ?? '—');
};

/** „21.10.2026" aus dem Berliner Tag. */
const datum = (wert: unknown): string => {
    const tag = buchungstag(typeof wert === 'string' ? wert : null);
    if (!tag) return 'ohne Datum';
    const [j, m, t] = tag.split('-');
    return `${t}.${m}.${j}`;
};

/** „Pos. 2.7.40 Baustellenbegehung" — aus dem LV, sonst aus der Belegzeile. */
const position = (serviceId: string | undefined, s: TorSchnappschuss): string => {
    const id = serviceId ?? '';
    const lv = (s.rahmen.leistungsverzeichnis ?? []).find((x) => x?.id === id);
    const zeile = s.projectLines.find((l) => l?.serviceId === id);
    const pos = String(lv?.lvPosition || zeile?.lvPosition || '').trim();
    const titel = String(lv?.title || zeile?.title || '').trim();
    if (pos && titel) return `Pos. ${pos} ${titel}`;
    if (pos) return `Pos. ${pos}`;
    if (titel) return titel;
    return `Leistung ${id || '?'}`;
};

/** Der Eintrag dieser Abweichung in einem Protokoll — ueber die Vergleichskennung. */
const eintragIn = (d: TorDiff, log: ServiceTransaction[]): ServiceTransaction | undefined => {
    const gesucht = kennungFuerVergleich(d.entryId);
    return log.find((e) => e?.serviceId === d.serviceId && kennungFuerVergleich(e?.id) === gesucht);
};

/** „vom 21.10.2026" bzw. „ohne Datum" — der Tag eines Eintrags, aus dem JSON, sonst aus dem Journal. */
const tagDesEintrags = (d: TorDiff, s: TorSchnappschuss, journalLog: ServiceTransaction[]): string => {
    const tag = datum((eintragIn(d, s.jsonLog) ?? eintragIn(d, journalLog))?.date);
    return tag === 'ohne Datum' ? tag : `vom ${tag}`;
};

/** Steht dieser Eintrag im JSON und kann er dort gar nicht ins Journal (`isJournalMappable`)? */
const nieImJournal = (d: TorDiff, s: TorSchnappschuss): boolean => {
    const e = eintragIn(d, s.jsonLog);
    return !!e && !isJournalMappable(e);
};

const PROTOKOLL = 'laut Projektprotokoll';
const JOURNAL = 'laut Buchungsjournal';

/** Eine Abweichung in einem Satz fuer den Ausstellen-Dialog. */
export const torPunkt = (d: TorDiff, s: TorSchnappschuss, journalLog: ServiceTransaction[]): string => {
    const pos = position(d.serviceId, s);
    switch (d.art) {
        // „Nur auf einer Seite zugeordnet" heisst nicht immer „fehlt": steht der
        // Eintrag auf beiden Seiten (andere Ziehreihenfolge, Tagesgrenze,
        // Schreibweise der uuid, Buchungsart), sagt der Satz das. Den genauen
        // Grund traegt die Meldung in bug_reports.
        case 'eintrag_fehlt_im_journal':
            return eintragIn(d, journalLog)
                ? `${pos}: Die Leistung ${tagDesEintrags(d, s, journalLog)} (Menge ${menge(d.json)}) wird ${PROTOKOLL} abgerechnet, ${JOURNAL} nicht.`
                : nieImJournal(d, s)
                  ? `${pos}: Die Leistung ${tagDesEintrags(d, s, journalLog)} (Menge ${menge(d.json)}) kann so nie ins Buchungsjournal (ohne Datum oder ohne lesbare Menge) — bitte die Leistung im Projekt datieren bzw. berichtigen und dann erneut ausstellen.`
                  : `${pos}: Die Leistung ${tagDesEintrags(d, s, journalLog)} (Menge ${menge(d.json)}) steht im Projektprotokoll, fehlt aber im Buchungsjournal.`;
        case 'eintrag_nur_im_journal':
            return eintragIn(d, s.jsonLog)
                ? `${pos}: Die Leistung ${tagDesEintrags(d, s, journalLog)} (Menge ${menge(d.journal)}) wird ${JOURNAL} abgerechnet, ${PROTOKOLL} nicht.`
                : `${pos}: Die Leistung ${tagDesEintrags(d, s, journalLog)} (Menge ${menge(d.journal)}) steht nur im Buchungsjournal, nicht im Projektprotokoll.`;
        case 'menge_weicht_ab':
            return `${pos}: Leistung ${tagDesEintrags(d, s, journalLog)} — zugeordnet ${menge(d.json)} ${PROTOKOLL}, ${menge(d.journal)} ${JOURNAL}.`;
        case 'tag_weicht_ab':
            return `${pos}: Leistungstag ${datum(d.json)} ${PROTOKOLL}, ${datum(d.journal)} ${JOURNAL}.`;
        case 'zeitraum_menge_weicht_ab':
            return `${pos}: im Leistungszeitraum offen ${menge(d.json)} ${PROTOKOLL}, ${menge(d.journal)} ${JOURNAL}.`;
        case 'stichtagsmenge_weicht_ab':
            return `${pos}: bis zum Stichtag erbracht ${menge(d.json)} ${PROTOKOLL}, ${menge(d.journal)} ${JOURNAL}.`;
        case 'undatiert_weicht_ab':
            return `${pos}: Menge ohne Datum ${menge(d.json)} ${PROTOKOLL}, ${menge(d.journal)} ${JOURNAL}.`;
        case 'buchungsart_weicht_ab': {
            const art = (v: unknown) => (v === 'abrechenbar' ? 'abrechenbare Leistung' : 'eine Buchung');
            return `${pos}: Der Eintrag ${tagDesEintrags(d, s, journalLog)} ist im Projektprotokoll ${art(d.json)}, im Buchungsjournal ${art(d.journal)}.`;
        }
        default:
            return `Belegzeile ${d.zeile ?? '?'}: Die Zuordnungen beider Wege passen nicht zueinander (interner Fehler).`;
    }
};

const PROJEKT_ZEILE = (kopf: TorKopf): string =>
    `Projekt ${projektName(kopf)}: Die Rechnung wurde nicht ausgestellt; für dieses Projekt wurde nichts gebucht und keine Rechnungsnummer vergeben.`;

const RAT_ADMIN =
    'Bleibt der Fehler, einen Administrator informieren (Einstellungen → Wartung → Tor-Notschalter).';

export const abweichungsAnzeige = (
    kopf: TorKopf,
    sperrend: TorDiff[],
    s: TorSchnappschuss,
    journalLog: ServiceTransaction[],
): TorSperrAnzeige => {
    const punkte = sperrend.slice(0, MAX_PUNKTE).map((d) => torPunkt(d, s, journalLog));
    if (sperrend.length > MAX_PUNKTE) punkte.push(`… und ${sperrend.length - MAX_PUNKTE} weitere Abweichungen.`);
    return {
        titel: `Ausstellen gesperrt — Projekt ${projektName(kopf)}: Die Zuordnung aus dem Buchungsjournal weicht vom Projektprotokoll ab.`,
        punkte,
        hinweis:
            'Die Rechnung wurde nicht ausgestellt; für dieses Projekt wurde nichts gebucht und keine Rechnungsnummer vergeben. ' +
            'Meist läuft das Buchungsjournal einer gerade erfassten oder geänderten Leistung noch nach — bitte in einigen Minuten erneut versuchen. ' +
            'Bleibt die Sperre, einen Administrator informieren: Die Meldung liegt bei den Fehlermeldungen vor; bei einer Störung des Buchungsjournals kann er unter Einstellungen → Wartung den Tor-Notschalter umlegen.',
    };
};

export const nichtLesbarAnzeige = (kopf: TorKopf, leer = false): TorSperrAnzeige => ({
    titel: 'Buchungsjournal nicht lesbar — bitte erneut versuchen.',
    punkte: [
        ...(leer
            ? [
                  `Das Buchungsjournal von Projekt ${projektName(kopf)} ist für Sie leer, das Projektprotokoll führt aber Leistungen. Meist fehlt der Projektzugriff: bitte das Projekt öffnen, den Zugriff freischalten (ggf. mit Begründung) und dann erneut ausstellen — oder die Rechnung von einem Administrator ausstellen lassen. Wurden die Leistungen gerade erst erfasst, steht ihre Zweitschrift ins Journal vielleicht noch aus: dann in einigen Minuten erneut versuchen.`,
              ]
            : []),
        PROJEKT_ZEILE(kopf),
    ],
    hinweis: RAT_ADMIN,
});

export const ausfallAnzeige = (kopf: TorKopf, fehler: unknown): TorSperrAnzeige => {
    const text = String((fehler as { message?: unknown } | null)?.message ?? fehler ?? '').slice(0, 200);
    return {
        titel: 'Die Prüfung am Rechnungstor ist ausgefallen — bitte erneut versuchen.',
        punkte: [PROJEKT_ZEILE(kopf), ...(text ? [`Fehler: ${text}`] : [])],
        hinweis: RAT_ADMIN,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Was nach bug_reports geht — dieselbe Form wie die Schattenmeldungen
// ═══════════════════════════════════════════════════════════════════════════
//
// Die erste Stackzeile beginnt mit „S5a Stufe 3b, Tor GESPERRT —" bzw.
// „S5a Stufe 3b, Tor ohne Sperre —"; die Tor-Ueberwachung zaehlt daran die
// Sperren und trennt sie von Meldungen alter Programmstaende („S5a Stufe 3a").

export const sperrMeldung = (kopf: TorKopf, ergebnis: TorVergleich, entscheid: TorEntscheid): TorMeldung => {
    const sperrend = new Set(entscheid.sperrend);
    return abweichungsMeldung(kopf, ergebnis, {
        was: 'Tor GESPERRT: Zuordnung aus dem Journal weicht ab',
        ersteZeile:
            'S5a Stufe 3b, Tor GESPERRT — die Ausstellung wurde vor jedem Schreiben angehalten (Journal-Modus, Entscheid 2).',
        zusatz: [`Sperrend: ${entscheid.sperrend.length} von ${ergebnis.diffs.length}`],
        reihenfolge: [...entscheid.sperrend, ...entscheid.ohneSperre],
        zeile: (d) => (sperrend.has(d) ? `- [${d.art}] ${d.text}` : `- (ohne Sperre) [${d.art}] ${d.text}`),
        json: { modus: 'journal', sperrend: entscheid.sperrend.length },
    });
};

export const ohneSperreMeldung = (
    kopf: TorKopf,
    ergebnis: TorVergleich,
    entscheid: TorEntscheid,
    anlass: 'ohne_sperrwirkung' | 'wiederholung',
): TorMeldung =>
    abweichungsMeldung(kopf, ergebnis, {
        was: 'Tor ohne Sperre: Zuordnung aus dem Journal weicht ab',
        ersteZeile:
            anlass === 'wiederholung'
                ? 'S5a Stufe 3b, Tor ohne Sperre — Wiederholung einer bereits gebuchten Ausstellung (die Buchungsreferenz steht schon im Projektprotokoll); nicht gesperrt, eingefroren wurde die Zuordnung aus dem JSON.'
                : 'S5a Stufe 3b, Tor ohne Sperre — keine Abweichung sperrt (Menge ohne Datum ohne gespeicherte Wahl am Beleg oder Zeitraumdeckel einer Leistung, der dieser Beleg nichts zuordnet); eingefroren wurde die Zuordnung aus dem Journal.',
        zusatz: [
            'Sperrend: 0 von ' + ergebnis.diffs.length,
            ...(anlass === 'wiederholung' && entscheid.sperrend.length > 0
                ? [`Bei einer ersten Ausstellung haetten ${entscheid.sperrend.length} gesperrt.`]
                : []),
        ],
        json: { modus: 'journal', sperrend: 0, anlass },
    });

/**
 * `leerTrotz` > 0: das Journal kam OHNE Zeile zurueck, das Projektprotokoll
 * fuehrt aber so viele abbildbare Eintraege — vermutlich Leserecht (Kopf,
 * „Ein leeres Journal …"). Titel und erste Zeile bleiben die des Lesefehlers,
 * damit die Tor-Ueberwachung beide als Lesefehler zaehlt.
 */
export const torNichtLesbarMeldung = (kopf: TorKopf, bereitsGebucht: boolean, leerTrotz = 0): TorMeldung => ({
    message: torMeldungsTitel('Tor GESPERRT: Buchungsjournal nicht lesbar', kopf),
    stack: kappeStack(
        [
            'S5a Stufe 3b, Tor GESPERRT — Buchungsjournal nicht lesbar (Entscheid 3), die Ausstellung wurde vor jedem Schreiben angehalten.',
            leerTrotz > 0
                ? `loadJournal lieferte KEINE Zeile, das Projektprotokoll fuehrt aber ${leerTrotz} abbildbare Leistungen — vermutlich fehlt das Leserecht (service_journal: is_admin() or can_access_project), verglichen wurde nichts.`
                : 'loadJournal lieferte null (Tabelle fehlt, kein Netz oder keine Berechtigung) — verglichen wurde nichts.',
            ...(bereitsGebucht ? ['Wiederholung einer bereits gebuchten Ausstellung.'] : []),
            JSON.stringify({
                ...kopf,
                modus: 'journal',
                bereitsGebucht,
                leerTrotz,
                online: typeof navigator !== 'undefined' ? navigator.onLine : null,
            }),
        ].join('\n'),
    ),
});

export const torAusfallMeldung = (kopf: TorKopf, fehler: unknown): TorMeldung => {
    const e = (fehler ?? {}) as { message?: string; stack?: string };
    return {
        message: torMeldungsTitel('Tor GESPERRT: Pruefung am Tor ausgefallen', kopf),
        stack: kappeStack(
            [
                'S5a Stufe 3b, Tor GESPERRT — die Pruefung am Tor ist ausgefallen, die Ausstellung wurde vor jedem Schreiben angehalten.',
                `Fehler: ${String(e.message ?? fehler).slice(0, 300)}`,
                e.stack ?? '',
                JSON.stringify({ ...kopf, modus: 'journal' }),
            ].join('\n'),
        ),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Das Tor
// ═══════════════════════════════════════════════════════════════════════════

export interface TorAmTorOptionen {
    /** Die Buchungsreferenz steht schon im Protokoll (`isAlreadyBooked`). */
    bereitsGebucht: boolean;
}

/**
 * Rechnet die Zuordnung aus dem Journal, vergleicht sie mit dem JSON-Weg und
 * gibt die Zeilen zurueck, mit denen ausgestellt wird — oder wirft
 * `TorGesperrtError`. Wartet auf das Journal (eine Rundreise je Projekt).
 *
 * Gemeldet wird ohne Warten (`void`): die Meldung wirft nie, und die Sperre
 * soll den Nutzer nicht erst nach einer zweiten Rundreise erreichen.
 */
export const torAmTor = async (
    eingang: SchattenAmTorEingang,
    optionen: TorAmTorOptionen,
    deps: SchattenAmTorDeps = STANDARD_TOR_DEPS,
): Promise<InvoiceLine[]> => {
    const kopf = torKopf(eingang);
    const ausfall = (fehler: unknown): TorGesperrtError => {
        void sicherMelden(deps, torAusfallMeldung(kopf, fehler));
        return new TorGesperrtError('ausfall', ausfallAnzeige(kopf, fehler), kopf.projectId);
    };

    // ── Schnappschuss, synchron — vor dem ersten `await` ─────────────────
    let schnappschuss: TorSchnappschuss;
    /**
     * Wie viele Eintraege des JSON-Protokolls im Journal stehen MUESSTEN: abbildbar
     * und keine Buchungsvorgaenge (BU-/VZ-/RN-/MB-). Die eigenen BU-Zeilen einer
     * Wiederholung fehlen dort zu Recht — ihre Zweitschrift kommt erst mit dem
     * fertigen Beleg —, und ein Storno-Projekt ohne Leistung hat nichts zu
     * vergleichen (Review 3b, Runde 2). Jetzt gezaehlt, im selben Moment wie der
     * Schnappschuss.
     */
    let abbildbareLeistungen: number;
    try {
        schnappschuss = schnappschussAmTor(eingang, kopf);
        abbildbareLeistungen = abbildbareLeistungenIn(schnappschuss.jsonLog);
    } catch (fehler) {
        throw ausfall(fehler);
    }

    // ── Das Journal — nicht lesbar heisst gesperrt (Entscheid 3) ─────────
    let journal: ServiceJournalRow[] | null = null;
    try {
        journal = await deps.ladeJournal(kopf.projectId);
    } catch {
        journal = null;
    }
    if (!journal) {
        void sicherMelden(deps, torNichtLesbarMeldung(kopf, optionen.bereitsGebucht));
        throw new TorGesperrtError('journal_nicht_lesbar', nichtLesbarAnzeige(kopf), kopf.projectId);
    }
    // Leer, obwohl das Protokoll abbildbare LEISTUNGEN fuehrt: Leserecht, nicht
    // Befund (Kopf, „Ein leeres Journal …").
    if (journal.length === 0 && abbildbareLeistungen > 0) {
        void sicherMelden(deps, torNichtLesbarMeldung(kopf, optionen.bereitsGebucht, abbildbareLeistungen));
        throw new TorGesperrtError('journal_nicht_lesbar', nichtLesbarAnzeige(kopf, true), kopf.projectId);
    }

    // ── Beide Wege vergleichen und entscheiden ───────────────────────────
    let weg: TorJournalWeg;
    let entscheid: TorEntscheid;
    try {
        weg = journalwegAmTor(schnappschuss, journal, deps.plane);
        entscheid = torEntscheid(weg.ergebnis, {
            includeUndated: schnappschuss.rahmen.includeUndated,
            zuzuordnen: zuzuordnendeLeistungen(schnappschuss.projectLines, schnappschuss.quantityOf),
        });
    } catch (fehler) {
        throw ausfall(fehler);
    }

    // Die Wiederholung einer gebuchten Ausstellung friert immer das JSON ein
    // und sperrt nie an einer Abweichung (Kopf, „Reihenfolge und Wiederholung").
    if (optionen.bereitsGebucht) {
        if (!weg.ergebnis.gleich) {
            void sicherMelden(deps, ohneSperreMeldung(kopf, weg.ergebnis, entscheid, 'wiederholung'));
        }
        return eingang.jsonLines;
    }

    if (weg.ergebnis.gleich) return weg.journalZeilen;

    if (entscheid.sperrend.length === 0) {
        void sicherMelden(deps, ohneSperreMeldung(kopf, weg.ergebnis, entscheid, 'ohne_sperrwirkung'));
        return weg.journalZeilen;
    }

    void sicherMelden(deps, sperrMeldung(kopf, weg.ergebnis, entscheid));
    throw new TorGesperrtError(
        'abweichung',
        abweichungsAnzeige(kopf, entscheid.sperrend, schnappschuss, weg.journalLog),
        kopf.projectId,
    );
};
